/**
 * SA-715 (spec T22): the first inbound email's header block, shown under
 * the conversation header on email threads. Pure presentation. Cc (SA-766)
 * is a row only when there is one; `dt`/`dd` stay direct children of the `dl`.
 * SA-1089: a long Cc list shows its first addresses and folds the rest
 * behind "Show N more", so a nine-address thread does not run the pane's
 * width in one line.
 */
import { useState } from 'react'
import { cn } from '@/utils'
import type { InboxEmailHeader as EmailHeader } from '@/types/inbox'

/** Addresses a Cc row shows before it folds. */
export const CC_SHOWN = 3

function sender(e: EmailHeader): string {
  return e.fromName ? `${e.fromName} <${e.fromAddr}>` : e.fromAddr
}

function received(iso: string | null): string {
  if (!iso) return 'Unknown'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

function CcList({ cc }: { cc: string[] }) {
  const [open, setOpen] = useState(false)
  const hidden = cc.length - CC_SHOWN
  if (hidden <= 0 || open) {
    return (
      <>
        {cc.join(', ')}
        {open && hidden > 0 && (
          <>
            {' '}
            <button type="button" aria-expanded="true" onClick={() => setOpen(false)} className="text-muted-foreground underline underline-offset-2">
              Show fewer
            </button>
          </>
        )}
      </>
    )
  }
  return (
    <>
      {cc.slice(0, CC_SHOWN).join(', ')}{' '}
      <button type="button" aria-expanded="false" onClick={() => setOpen(true)} className="text-muted-foreground underline underline-offset-2">
        Show {hidden} more
      </button>
    </>
  )
}

export function InboxEmailHeader({ email, className }: { email: EmailHeader; className?: string }) {
  return (
    <dl
      role="group"
      aria-label="Email header"
      className={cn('grid grid-cols-[minmax(6.5rem,max-content)_minmax(0,1fr)] gap-x-3 gap-y-0.5 px-4 py-2 text-xs border-b border-border', className)}
    >
      <dt className="text-muted-foreground">From</dt>
      <dd className="min-w-0 [overflow-wrap:anywhere]">{sender(email)}</dd>
      <dt className="text-muted-foreground">To</dt>
      <dd className="min-w-0 [overflow-wrap:anywhere]">{email.to.length ? email.to.join(', ') : 'Unknown'}</dd>
      {email.cc.length > 0 && (
        <>
          <dt className="text-muted-foreground">Cc</dt>
          <dd className="min-w-0 [overflow-wrap:anywhere]"><CcList cc={email.cc} /></dd>
        </>
      )}
      <dt className="text-muted-foreground">Subject</dt>
      <dd className="min-w-0 [overflow-wrap:anywhere]">{email.subject || '(no subject)'}</dd>
      <dt className="text-muted-foreground">Received</dt>
      <dd className="min-w-0">{received(email.receivedAt)}</dd>
    </dl>
  )
}
